import React, { useState, useEffect, useCallback } from "react";
import Menu from "../Menu";
import Header from "../Header";
import { http } from "../../services/http";
import { useAlert } from "react-alert";
import { 
  PagingState, 
  CustomPaging, 
  SortingState,
  IntegratedSorting
} from "@devexpress/dx-react-grid";
import { 
  Grid, 
  Table, 
  TableHeaderRow, 
  PagingPanel, 
  TableColumnVisibility 
} from "@devexpress/dx-react-grid-material-ui";
import authenticatedUserContext from "../security/AuthenticatedUserContext";

import { Button, Tooltip, IconButton } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import TableCell from "@material-ui/core/TableCell";
import { Add, Edit, Delete, History } from "@material-ui/icons";
import FilterPanel from "./FilterPanel";
import RegraFormModal from "./RegraFormModal";
import HistoricoDialog from "./HistoricoDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import ConflictDialog from "./ConflictDialog";
import { formatDate } from "./regrasFaturamentoUtils";

const getRowId = (row) => row.id;
const api_resource = "/regras-faturamento";

const RegrasFaturamentoTable = () => {
  const alert = useAlert();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(15);
  const [totalItens, setTotalItens] = useState(0);
  const [rows, setRows] = useState([]);
  const [sorting, setSorting] = useState([]);

  // Painel de filtros
  const [filterPanel, setFilterPanel] = useState({
    BusinessUnit: null,
    Unidade: null,
    FormaCobranca: null,
    Cirurgiao: null,
    BPLID: null,
    StartDateFrom: '',
    StartDateTo: ''
  });
  const [defaultHiddenColumnNames] = useState(["id"]);
  
  // Modal state
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRegra, setCurrentRegra] = useState({
    id: null,
    Unidade: '',
    FormaCobranca: '',
    Cirurgiao: '',
    StartDate: '',
    EndDate: '',
    BPLID: '',
    BusinessUnit: ''
  });

  // Modal de histórico
  const [openHistorico, setOpenHistorico] = useState(false);
  const [historicoData, setHistoricoData] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  // Dialog de conflito
  const [conflictError, setConflictError] = useState(null);

  // Dialog de confirmação de exclusão
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, row: null });

  // Loading da tabela e do modal de edição
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Erros de validação do formulário
  const [formErrors, setFormErrors] = useState({
    FormaCobranca: false,
    BPLID: false,
    StartDate: false
  });

  // Autocomplete options
  const [unidadesOptions, setUnidadesOptions] = useState([]);
  const [cirurgioesOptions, setCirurgioesOptions] = useState([]);
  const [formasCobrancaOptions, setFormasCobrancaOptions] = useState([]);
  const [loadingUnidades, setLoadingUnidades] = useState(false);
  const [loadingCirurgioes, setLoadingCirurgioes] = useState(false);
  const [loadingFormasCobranca, setLoadingFormasCobranca] = useState(false);
  const [bplidOptions, setBplidOptions] = useState([]);
  const [loadingBplids, setLoadingBplids] = useState(false);

  const [tableColumnExtensions] = useState([
    { columnName: "id", width: 80 },
    { columnName: "BusinessUnit", width: '15%' },
    { columnName: "UnidadeName", width: '15%' },
    { columnName: "FormaCobrancaName", width: '15%' },
    { columnName: "CirurgiaoName", width: '15%' },
    { columnName: "StartDate", width: '12%' },
    { columnName: "EndDate", width: '12%' },
    { columnName: "BPLID", width: '10%' },
    { columnName: "acoes", width: '10%' }
  ]);

  const columns = [
    { name: "id", title: "ID" },
    { name: "BusinessUnit", title: "Unidade de Negócio" },
    { name: "UnidadeName", title: "Unidade" },
    { name: "FormaCobrancaName", title: "Forma Cobrança" },
    { name: "CirurgiaoName", title: "Cirurgião" },
    { name: "StartDate", title: "Data Início" },
    { name: "EndDate", title: "Data Fim" },
    { name: "BPLID", title: "BPLID" },
    { name: "acoes", title: "Ações" }
  ];

  // Monta query string a partir do painel de filtros
  function buildQueryString(page, fp) {
    const params = [`page=${page + 1}`, `limit=${pageSize}`];
    if (fp.BusinessUnit) params.push(`BusinessUnit=${fp.BusinessUnit.value}`);
    if (fp.Unidade) params.push(`Unidade=${fp.Unidade.id}`);
    if (fp.FormaCobranca) params.push(`FormaCobranca=${fp.FormaCobranca.id}`);
    if (fp.Cirurgiao) params.push(`Cirurgiao=${fp.Cirurgiao.id}`);
    if (fp.BPLID && fp.BPLID.id !== undefined && fp.BPLID.id !== null) {
      params.push(`BPLID=${encodeURIComponent(fp.BPLID.id)}`);
    }
    if (fp.StartDateFrom) params.push(`StartDateFrom=${fp.StartDateFrom}`);
    if (fp.StartDateTo) params.push(`StartDateTo=${fp.StartDateTo}`);
    return `?${params.join('&')}`;
  }

  // Buscar regras
  const getRegras = useCallback((page, fp) => {
    const queryString = buildQueryString(page, fp || filterPanel);
    setLoadingTable(true);
    http.get(`${api_resource}${queryString}`)
      .then((response) => {
        const data = response.data.data || [];
        // Formata as datas apenas para exibição, mantém original para edição
        const formattedData = data.map(regra => ({
          ...regra,
          StartDateOriginal: regra.StartDate, // Mantém formato original
          EndDateOriginal: regra.EndDate,     // Mantém formato original
          StartDate: formatDate(regra.StartDate), // Para exibição
          EndDate: formatDate(regra.EndDate)       // Para exibição
        }));
        setRows(formattedData);
        setTotalItens(parseInt(response.data.total, 10));
      })
      .catch((error) => {
        console.error(error);
        alert.error("Erro ao carregar regras de faturamento");
      })
      .finally(() => setLoadingTable(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  // Carregar todas as unidades
  function loadUnidades() {
    setLoadingUnidades(true);
    http.get(`/regras-faturamento/units`)
      .then((response) => {
        if (response?.data?.data) {
          setUnidadesOptions(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar unidades:", error);
        alert.error("Erro ao carregar unidades");
      })
      .finally(() => {
        setLoadingUnidades(false);
      });
  }

  // Carregar todos os cirurgiões
  function loadCirurgioes() {
    setLoadingCirurgioes(true);
    http.get(`/regras-faturamento/surgeons`)
      .then((response) => {
        if (response?.data?.data) {
          setCirurgioesOptions(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar cirurgiões:", error);
        alert.error("Erro ao carregar cirurgiões");
      })
      .finally(() => {
        setLoadingCirurgioes(false);
      });
  }

  // Buscar formas de cobrança
  function loadFormasCobranca() {
    setLoadingFormasCobranca(true);
    http.get(`/regras-faturamento/schedule-payment-types`)
      .then((response) => {
        if (response?.data?.data) {
          setFormasCobrancaOptions(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar formas de cobrança:", error);
        alert.error("Erro ao carregar formas de cobrança");
      })
      .finally(() => {
        setLoadingFormasCobranca(false);
      });
  }

  // Buscar BPLIDs a partir dos parâmetros do bot Faturamento D2i SAP
  function loadBplids() {
    setLoadingBplids(true);
    http.get(`/regras-faturamento/bplids`)
      .then((response) => {
        if (response?.data?.data) {
          setBplidOptions(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar BPLIDs:", error);
        alert.error("Erro ao carregar BPLIDs");
      })
      .finally(() => {
        setLoadingBplids(false);
      });
  }

  // Atualiza campo do painel de filtros (sem busca automática)
  const handleFilterChange = useCallback((field, value) => {
    setFilterPanel(prev => ({ ...prev, [field]: value }));
  }, []);

  function handleSearch() {
    setPageIndex(0);
    getRegras(0, filterPanel);
  }

  function handleClearFilters() {
    const empty = { BusinessUnit: null, Unidade: null, FormaCobranca: null, Cirurgiao: null, BPLID: null, StartDateFrom: '', StartDateTo: '' };
    setFilterPanel(empty);
    setPageIndex(0);
    getRegras(0, empty);
  }

  function handlePage(page) {
    setPageIndex(page);
    getRegras(page, filterPanel);
  }

  // Abrir modal para criar
  const handleClickAdd = () => {
    setEditMode(false);
    setCurrentRegra({
      id: null,
      Unidade: '',
      FormaCobranca: '',
      Cirurgiao: '',
      StartDate: '',
      EndDate: '',
      BPLID: '',
      BusinessUnit: ''
    });
    setFormErrors({ FormaCobranca: false, BPLID: false, StartDate: false });
    setOpen(true);
  };

  // Abrir modal para editar
  const handleClickEdit = (row) => {
    setEditMode(true);
    setFormErrors({ FormaCobranca: false, BPLID: false, StartDate: false });
    setLoadingEdit(true);
    setOpen(true);

    // Busca os dados originais da API para ter certeza que tem formato correto
    http.get(`${api_resource}/${row.id}`)
      .then((response) => {
        const regra = response.data;
        setCurrentRegra({
          id: regra.id,
          Unidade: regra.Unidade || '',
          FormaCobranca: regra.FormaCobranca || '',
          Cirurgiao: regra.Cirurgiao || '',
          StartDate: regra.StartDate || '', // Backend retorna YYYY-MM-DD
          EndDate: regra.EndDate || '',     // Backend retorna YYYY-MM-DD
          BPLID: regra.BPLID || '',
          BusinessUnit: regra.BusinessUnit || ''
        });
      })
      .catch((error) => {
        console.error("Erro ao buscar regra:", error);
        alert.error("Erro ao carregar dados da regra");
        setOpen(false);
      })
      .finally(() => setLoadingEdit(false));
  };

  // Abrir dialog de confirmação de exclusão
  const handleDeleteClick = (row) => {
    setDeleteConfirm({ open: true, row });
  };

  // Confirmar exclusão
  const handleConfirmDelete = () => {
    const { row } = deleteConfirm;
    if (!row) return;
    setDeleteConfirm({ open: false, row: null });
    http.delete(`${api_resource}/${row.id}`)
      .then(() => {
        alert.success("Regra excluída com sucesso!");
        getRegras(pageIndex, filterPanel);
      })
      .catch((error) => {
        console.error("Erro ao deletar regra:", error);
        alert.error("Erro ao excluir regra");
      });
  };

  // Abrir histórico
  const handleOpenHistorico = (row) => {
    setLoadingHistorico(true);
    setOpenHistorico(true);
    http.get(`${api_resource}/${row.id}/audit`)
      .then((response) => {
        setHistoricoData(response.data.data || []);
      })
      .catch((error) => {
        console.error("Erro ao buscar histórico:", error);
        alert.error("Erro ao carregar histórico");
      })
      .finally(() => {
        setLoadingHistorico(false);
      });
  };

  // Fechar histórico
  const handleCloseHistorico = () => {
    setOpenHistorico(false);
    setHistoricoData([]);
  };

  // Fechar modal
  const handleClose = () => {
    setOpen(false);
    setLoadingEdit(false);
    setFormErrors({ FormaCobranca: false, BPLID: false, StartDate: false });
  };

  // Salvar regra
  const handleSave = () => {
    const errors = {
      FormaCobranca: !currentRegra.FormaCobranca,
      BPLID: !currentRegra.BPLID || currentRegra.BPLID.toString().trim() === '',
      StartDate: !currentRegra.StartDate
    };
    setFormErrors(errors);

    if (errors.FormaCobranca || errors.BPLID || errors.StartDate) {
      alert.error("Preencha todos os campos obrigatórios");
      return;
    }

    const data = {
      Unidade: currentRegra.Unidade,
      FormaCobranca: currentRegra.FormaCobranca,
      Cirurgiao: currentRegra.Cirurgiao,
      StartDate: currentRegra.StartDate,
      EndDate: currentRegra.EndDate,
      BPLID: currentRegra.BPLID,
      BusinessUnit: currentRegra.BusinessUnit
    };

    const handleError = (error, defaultMsg) => {
      console.error(defaultMsg, error);
      const responseData = error?.messages?.[0] ?? error?.response?.data;
      const conflitos = responseData?.conflitos;
      const message = responseData?.message || defaultMsg;
      if (conflitos && conflitos.length > 0) {
        setConflictError({ message, conflitos });
      } else {
        alert.error(message);
      }
    };

    if (editMode) {
      http.put(`${api_resource}/${currentRegra.id}`, data)
        .then(() => {
          alert.success("Regra atualizada com sucesso!");
          getRegras(pageIndex, filterPanel);
          handleClose();
        })
        .catch((error) => handleError(error, "Erro ao atualizar regra"));
    } else {
      http.post(api_resource, data)
        .then(() => {
          alert.success("Regra criada com sucesso!");
          getRegras(pageIndex, filterPanel);
          handleClose();
        })
        .catch((error) => handleError(error, "Erro ao criar regra"));
    }
  };

  // Custom cell para ações
  const AcoesCell = ({ row }) => (
    <TableCell>
      <Tooltip title="Histórico">
        <IconButton 
          size="small" 
          style={{ color: '#17a2b8' }}
          onClick={() => handleOpenHistorico(row)}
        >
          <History />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton 
          size="small" 
          color="primary"
          onClick={() => handleClickEdit(row)}
          disabled={!authenticatedUserContext.allow_edit('Regra de Faturamento') || loadingEdit}
        >
          <Edit />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton 
          size="small" 
          color="secondary"
          onClick={() => handleDeleteClick(row)}
          disabled={!authenticatedUserContext.allow_edit('Regra de Faturamento')}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    </TableCell>
  );

  const CellComponent = (props) => {
    const { column, row } = props;
    if (column.name === 'acoes') {
      return <AcoesCell row={row} />;
    }
    if (column.name === 'BusinessUnit') {
      let label = '';
      if (row.BusinessUnit === 'CUSTOMIZED') label = 'PERSONALIZADO';
      else if (row.BusinessUnit === 'STANDARDIZED') label = 'PADRONIZADO';
      else if (row.BusinessUnit === 'DOR') label = 'DOR';
      else label = row.BusinessUnit || '';

      return (
        <TableCell>
          {label}
        </TableCell>
      );
    }
    if (column.name === 'BPLID') {
      const option = bplidOptions.find(opt => String(opt.id) === String(row.BPLID));
      return (
        <TableCell>
          {option ? option.name : row.BPLID}
        </TableCell>
      );
    }
    return <Table.Cell {...props} />;
  };

  useEffect(() => {
    getRegras(0, filterPanel);
    loadFormasCobranca();
    loadUnidades();
    loadCirurgioes();
    loadBplids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id="wrapper">
      <Menu />
      <div id="page-wrapper" className="gray-bg">
        <Header/>
        <div className="row wrapper border-bottom white-bg page-heading">
          <div className="col-lg-10">
            <h2><i className="fa fa-file-text-o"></i> Regras de Faturamento</h2>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <span>Cadastro e configuração de regras de faturamento</span>
              </li>
            </ol>
          </div>
          <div className="col-lg-2" style={{ paddingTop: '20px' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleClickAdd}
              fullWidth
              disabled={!authenticatedUserContext.allow_edit('Regra de Faturamento')}
            >
              Nova Regra
            </Button>
          </div>
        </div>
        
        <div className="wrapper wrapper-content animated fadeInRight">
          <div className="row">
            <div className="col-lg-12">

              {/* Painel de Filtros */}
              <FilterPanel
                filterPanel={filterPanel}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onClearFilters={handleClearFilters}
                unidadesOptions={unidadesOptions}
                formasCobrancaOptions={formasCobrancaOptions}
                cirurgioesOptions={cirurgioesOptions}
                loadingUnidades={loadingUnidades}
                loadingFormasCobranca={loadingFormasCobranca}
                loadingCirurgioes={loadingCirurgioes}
                bplidOptions={bplidOptions}
                loadingBplids={loadingBplids}
              />

              {/* Tabela */}
              <div className="ibox">
                <div className="ibox-content">
                  {loadingTable ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                      <CircularProgress />
                    </div>
                  ) : (
                  <Grid rows={rows} columns={columns} getRowId={getRowId}>
                    <PagingState 
                      currentPage={pageIndex} 
                      pageSize={pageSize} 
                      onCurrentPageChange={handlePage} 
                    />
                    <SortingState
                      sorting={sorting}
                      onSortingChange={setSorting}
                    />
                    <IntegratedSorting />
                    <Table 
                      columnExtensions={tableColumnExtensions}
                      cellComponent={CellComponent}
                    />
                    <TableHeaderRow showSortingControls />
                    <TableColumnVisibility 
                      defaultHiddenColumnNames={defaultHiddenColumnNames} 
                    />
                    <CustomPaging totalCount={totalItens} />
                    <PagingPanel />
                  </Grid>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        <HistoricoDialog
          open={openHistorico}
          onClose={handleCloseHistorico}
          loading={loadingHistorico}
          historicoData={historicoData}
        />

        <RegraFormModal
          open={open}
          onClose={handleClose}
          editMode={editMode}
          loadingEdit={loadingEdit}
          currentRegra={currentRegra}
          onRegraChange={setCurrentRegra}
          formErrors={formErrors}
          onFormErrorChange={setFormErrors}
          unidadesOptions={unidadesOptions}
          formasCobrancaOptions={formasCobrancaOptions}
          cirurgioesOptions={cirurgioesOptions}
          loadingUnidades={loadingUnidades}
          loadingFormasCobranca={loadingFormasCobranca}
          loadingCirurgioes={loadingCirurgioes}
            bplidOptions={bplidOptions}
            loadingBplids={loadingBplids}
          onSave={handleSave}
        />

        <DeleteConfirmDialog
          open={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, row: null })}
          onConfirm={handleConfirmDelete}
        />

        <ConflictDialog
          conflictError={conflictError}
          onClose={() => setConflictError(null)}
          unidadesOptions={unidadesOptions}
          formasCobrancaOptions={formasCobrancaOptions}
          cirurgioesOptions={cirurgioesOptions}
        />
      </div>
    </div>
  );
};

export default RegrasFaturamentoTable;
